export const extractLocationFromUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    // Pattern 1: Extract from /place/ URL with coordinates
    // Example: /place/Times+Square/@40.7128,-74.0060,15z
    const placeWithCoords = url.match(/\/place\/([^/@]+)\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (placeWithCoords) {
      return {
        address: decodeURIComponent(placeWithCoords[1]).replace(/\+/g, " "),
        lat: parseFloat(placeWithCoords[2]),
        lng: parseFloat(placeWithCoords[3]),
        rawUrl: url,
      };
    }

    // Pattern 2: Extract from @ coordinates (without /place/)
    // Example: @40.7128,-74.0060,15z
    const coordOnly = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (coordOnly) {
      return {
        lat: parseFloat(coordOnly[1]),
        lng: parseFloat(coordOnly[2]),
        rawUrl: url,
      };
    }

    // Pattern 3: Extract from /dir/ (directions) URLs
    // Example: /dir/New+York/Boston/@40.7128,-74.0060
    const dirMatch = url.match(/\/dir\/[^/@]*\/[^/@]*\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (dirMatch) {
      return {
        lat: parseFloat(dirMatch[1]),
        lng: parseFloat(dirMatch[2]),
        rawUrl: url,
      };
    }

    // Pattern 4: Extract from query parameter q=lat,lng
    // Example: ?q=40.7128,-74.0060
    const qCoords = url.match(/[?&]q=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);

    if (qCoords) {
      return {
        lat: parseFloat(qCoords[1]),
        lng: parseFloat(qCoords[2]),
        rawUrl: url,
      };
    }

    // Pattern 5: Extract from query parameter with address
    // Example: ?q=Empire+State+Building
    const qAddress = url.match(/[?&]q=([^&]+)/);

    if (qAddress) {
      const addressParam = decodeURIComponent(qAddress[1]).replace(/\+/g, " ");

      // Check if it's coordinates in the address parameter
      const coordInQ = addressParam.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);

      if (coordInQ) {
        return {
          lat: parseFloat(coordInQ[1]),
          lng: parseFloat(coordInQ[2]),
          rawUrl: url,
        };
      }

      // It's an address or location name
      return { address: addressParam, rawUrl: url };
    }

    // Pattern 6: Extract from /search/ URLs with coordinates
    // Example: /search/restaurants/@40.7128,-74.0060,15z
    const searchMatch = url.match(/\/search\/[^/@]+\/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (searchMatch) {
      return {
        lat: parseFloat(searchMatch[1]),
        lng: parseFloat(searchMatch[2]),
        rawUrl: url,
      };
    }

    // Pattern 7: Extract from /data/ URLs with !3d and !4d markers
    // Example: /data=...!3d40.7128!4d-74.0060
    const dataMatch = url.match(/!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/);

    if (dataMatch) {
      return {
        lat: parseFloat(dataMatch[1]),
        lng: parseFloat(dataMatch[2]),
        rawUrl: url,
      };
    }

    // Pattern 8: Extract from ll parameter (Apple Maps style)
    // Example: ?ll=40.7128,-74.0060
    const llMatch = url.match(/[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (llMatch) {
      return {
        lat: parseFloat(llMatch[1]),
        lng: parseFloat(llMatch[2]),
        rawUrl: url,
      };
    }

    // Pattern 8a: Extract from center parameter
    // Example: ?center=40.7128,-74.0060&zoom=12
    const centerMatch = url.match(/[?&]center=(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (centerMatch) {
      return {
        lat: parseFloat(centerMatch[1]),
        lng: parseFloat(centerMatch[2]),
        rawUrl: url,
      };
    }

    // Pattern 8b: Extract from sll parameter (source lat/lng)
    // Example: ?sll=40.7128,-74.0060
    const sllMatch = url.match(/[?&]sll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);

    if (sllMatch) {
      return {
        lat: parseFloat(sllMatch[1]),
        lng: parseFloat(sllMatch[2]),
        rawUrl: url,
      };
    }

    // Pattern 8c: Extract from daddr parameter (destination address)
    // Example: ?daddr=Empire+State+Building or ?daddr=40.7128,-74.0060
    const daddrMatch = url.match(/[?&]daddr=([^&]+)/);

    if (daddrMatch) {
      const daddrParam = decodeURIComponent(daddrMatch[1]).replace(/\+/g, " ");

      // Check if it's coordinates
      const coordInDaddr = daddrParam.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);

      if (coordInDaddr) {
        return {
          lat: parseFloat(coordInDaddr[1]),
          lng: parseFloat(coordInDaddr[2]),
          rawUrl: url,
        };
      }

      // It's an address or location name
      return { address: daddrParam, rawUrl: url };
    }

    // Pattern 8d: Extract from saddr parameter (source address)
    // Example: ?saddr=Times+Square
    const saddrMatch = url.match(/[?&]saddr=([^&]+)/);

    if (saddrMatch) {
      const saddrParam = decodeURIComponent(saddrMatch[1]).replace(/\+/g, " ");

      // Check if it's coordinates
      const coordInSaddr = saddrParam.match(/^(-?\d+\.?\d*),\s*(-?\d+\.?\d*)$/);

      if (coordInSaddr) {
        return {
          lat: parseFloat(coordInSaddr[1]),
          lng: parseFloat(coordInSaddr[2]),
          rawUrl: url,
        };
      }

      // It's an address or location name
      return { address: saddrParam, rawUrl: url };
    }

    // Pattern 8e: Extract from /maps/search/ without coordinates (search query)
    // Example: /search/pizza+near+me or /search/Central+Park
    const searchQueryMatch = url.match(/\/search\/([^/@?#]+)/);

    if (searchQueryMatch) {
      const searchQuery = decodeURIComponent(searchQueryMatch[1]).replace(/\+/g, " ").trim();

      if (searchQuery.length > 3 && !/^(true|false|null|undefined)$/i.test(searchQuery)) {
        return {
          address: searchQuery,
          rawUrl: url,
        };
      }
    }

    // Pattern 9: Extract from malformed /place/ URL - FLEXIBLE VERSION
    // Handles various address formats with optional components
    // Examples:
    // - Full: /place/386+Lafayette+St,Denver,CO,80218
    // - Partial: /place/Denver,CO
    // - Location name: /place/Central+Park
    const placeContent = url.match(/\/place\/([^@?#]+)/);

    if (placeContent) {
      const rawPlace = decodeURIComponent(placeContent[1]).replace(/\+/g, " ").trim();

      // Filter out garbage prefixes like "true,true,true"
      const cleanedPlace = rawPlace.replace(/^(?:true|false|null|undefined)(?:,\s*(?:true|false|null|undefined))*,\s*/gi, "");

      // Skip if nothing left after cleaning or too short
      if (cleanedPlace.length > 3) {
        // Split by comma to analyze parts
        const parts = cleanedPlace
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);

        if (parts.length > 0) {
          // Filter out garbage parts
          const addressComponents = [];

          for (let i = 0; i < parts.length; i++) {
            const part = parts[i];

            // Skip if it's garbage keywords
            if (/^(true|false|null|undefined)$/i.test(part)) {
              continue;
            }

            // Skip standalone single/double/triple digit numbers at the start (likely garbage)
            // But keep ZIP codes (4-6 digits) and numbers that are part of addresses
            if (/^\d{1,3}$/.test(part) && i === 0) {
              continue;
            }

            // Add valid part
            addressComponents.push(part);
          }

          if (addressComponents.length > 0) {
            // Build address from components
            const fullAddress = addressComponents.join(", ");

            return {
              address: fullAddress,
              rawUrl: url,
            };
          }
        }
      }
    }

    // Pattern 10: Extract any location name from /place/ (generic fallback)
    // Example: /place/Empire+State+Building
    const placeNameMatch = url.match(/\/place\/([^/@?#]+)/);

    if (placeNameMatch) {
      const placeName = decodeURIComponent(placeNameMatch[1]).replace(/\+/g, " ").trim();

      // Filter out invalid place names (like just "true" or very short strings)
      if (placeName.length > 3 && !/^(true|false|null|undefined)$/i.test(placeName)) {
        return {
          address: placeName,
          rawUrl: url,
        };
      }
    }

    // No valid location found
    return null;
  } catch (error) {
    console.error("Error extracting location from URL:", error);

    return null;
  }
};

export const validateCoordinates = (lat, lng) => {
  return lat !== undefined && lng !== undefined && !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

export const normalizeToEmbedUrl = (url) => {
  if (!url) {
    // No URL provided, show default US location
    return `https://www.google.com/maps?q=United+States&output=embed`;
  }

  try {
    // If it's already an embed URL, return as is
    if (url.includes("/maps/embed")) {
      return url;
    }

    // Extract location data
    const location = extractLocationFromUrl(url);

    if (!location) {
      return `https://www.google.com/maps?q=United+States&output=embed`;
    }

    // Priority 1: If we have valid coordinates, use them (most reliable)
    if (validateCoordinates(location.lat, location.lng)) {
      return `https://www.google.com/maps?q=${location.lat},${location.lng}&output=embed`;
    }

    // Priority 2: If we have an address or location name, use it
    if (location.address) {
      const encodedAddress = encodeURIComponent(location.address);

      return `https://www.google.com/maps?q=${encodedAddress}&output=embed`;
    }

    // Fallback: Show default US location if location object exists but has no valid data
    console.warn("Location object found but no valid coordinates or address, showing default US location:", location);

    return `https://www.google.com/maps?q=United+States&output=embed`;
  } catch (error) {
    console.error("Error normalizing URL to embed format:", error);

    // Fallback on error: Show default US location
    return `https://www.google.com/maps?q=United+States&output=embed`;
  }
};

export const extractEmbedMapUrl = (message) => {
  if (!message) {
    return null;
  }

  // Extract Google Maps URL from message
  const urlMatch = message?.match(/https:\/\/www\.google\.com\/maps[^ "]+/);
  let extractedUrl = urlMatch ? urlMatch[0] : null;

  if (!extractedUrl) {
    return null;
  }

  // Remove trailing period if exists (common in sentences)
  if (extractedUrl.endsWith(".")) {
    extractedUrl = extractedUrl.slice(0, -1);
  }

  // Normalize the extracted URL to embeddable format
  return normalizeToEmbedUrl(extractedUrl);
};

export const extractMessageText = (message) => {
  if (!message) {
    return "";
  }

  return (message?.split("https://")[0] || "").trim();
};
